import { TAG } from 'core/constants/constant';
import { Helmet } from 'react-helmet-async';

export interface IProps {
  /// should optimally have length <= 60 characters
  title?: string

  /// should optimally have 150 <= length <= 160 characters
  description?: string

  /// can be relative or absolute or will use a fallback image if not set
  imageUrl?: string

  keywords?: string

  structuredData?: any;
}

/// Drop-in SEO handling of metadata for your page. Does handle vanilla meta tags,
/// opengraph tags for facebook/linkedin/... and twitter cards tags for
/// twitter/slack/... .
export const MetaTags = (props: IProps) => {
  return (
    <>
	  <CORSMetaTags />
      { !process.env.REACT_APP_ENV?.startsWith('prod') && <NoRobotMetaTags /> }
      <TitleMetaTags {...props} />
      <DescriptionMetaTags {...props} />
      <KeywordMetaTags {...props} />
      <StructuredData structuredData={props.structuredData} />
    </>
  );
}

// apply some branding addendum automatically if not deactivated
const TitleMetaTags = ({ title }: IProps) => {
  const safeTitle = typeof title === 'string' ? title.trim() : '';
  if (!safeTitle) return null;
  return (
    <Helmet>
      <title>{safeTitle}</title>
      <meta property="og:title" content={safeTitle} />
      <meta property="twitter:title" content={safeTitle} />
    </Helmet>
  );
}

// use the given description as-is
const DescriptionMetaTags = ({ description = TAG.description }: IProps) => {
  return (
    <Helmet>
      <meta name="description" content={description} />
      <meta property="og:description" content={description} />
      <meta property="twitter:description" content={description} />
    </Helmet>
  );
}


// use the given keywords as-is
const KeywordMetaTags = ({ keywords }: IProps) => {
  return (
    <Helmet>
      <meta name="keywords" content={keywords} />
      <meta property="og:keywords" content={keywords} />
      <meta property="twitter:keywords" content={keywords} />
    </Helmet>
  );
}
const CORSMetaTags = () => {
  return (
    <Helmet>
		<meta
		  http-equiv="Content-Security-Policy"
		  content="
		    default-src 'self';
		    script-src 
				'self' 
				'unsafe-inline' 
				https://js.finix.com 
				https://js.stripe.com 
				https://js.braintreegateway.com
				https://www.googletagmanager.com 
				https://www.google-analytics.com 
				https://googleads.g.doubleclick.net 
				https://www.googleadservices.com 
				https://*.google.com 
				https://*.google.co.in;
		    frame-src
		      'self'
		      https://*.finix.com
		      https://js.finix.com
		      https://js.stripe.com
		      https://hooks.stripe.com
		      https://*.braintreegateway.com
		      https://assets.braintreegateway.com
		      https://www.googletagmanager.com
		      https://googleads.g.doubleclick.net
		      https://www.googleadservices.com
		      https://td.doubleclick.net;
		    child-src 'self' https://js.finix.com https://*.finix.com https://js.stripe.com https://hooks.stripe.com https://*.braintreegateway.com https://assets.braintreegateway.com https://*.bigrentals.com https://www.googletagmanager.com;
		    connect-src
		      'self'
		      https://*.finix.com
		      https://api.ipify.org
		      https://*.bigrentals.com
		      https://api.stripe.com
		      https://api.braintreegateway.com
		      https://*.braintree-api.com
		      https://client-analytics.braintreegateway.com
		      https://*.uat.bigrentals.com
		      https://*.dev.bigrentals.com
		      https://*.amazonaws.com
		      https://cdn.plaid.com
		      https://www.googletagmanager.com
		      https://www.google-analytics.com
		      https://*.google-analytics.com
		      https://analytics.google.com
		      https://*.analytics.google.com
		      https://www.google.com
		      https://*.google.com
		      https://*.google.co.in
		      https://stats.g.doubleclick.net
		      https://googleads.g.doubleclick.net
		      https://*.doubleclick.net
		      https://www.googleadservices.com
		      https://pagead2.googlesyndication.com
		      https://*.cloudfront.net
		      https://fonts.googleapis.com
		      https://fonts.gstatic.com;
		    img-src
		      'self'
		      data:
		      blob:
		      https:
		      https://*.cloudfront.net
		      https://www.google-analytics.com
		      https://*.google-analytics.com
		      https://analytics.google.com
		      https://*.analytics.google.com
		      https://googleads.g.doubleclick.net
		      https://stats.g.doubleclick.net
		      https://*.doubleclick.net
		      https://www.googleadservices.com
		      https://pagead2.googlesyndication.com;
		    style-src 'self' 'unsafe-inline' https://fonts.googleapis.com https://*.braintreegateway.com;
		    style-src-elem 'self' 'unsafe-inline' https://fonts.googleapis.com https://*.braintreegateway.com;
		    font-src 'self' https://fonts.gstatic.com https://fonts.googleapis.com;
		  "
		/>
    </Helmet>
  );
}
const NoRobotMetaTags = () => {
  return (
    <Helmet>
      <meta name="robots" content="noindex" />
    </Helmet>
  );
}

const StructuredData = ({ structuredData }:any) => {
  const hasStructure = !!structuredData && Object.keys(structuredData).length > 0;
  return hasStructure ? (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
    </Helmet>
  ) : <></>;
}
